/**
 * Icon mapping for Scene Tree nodes.
 *
 * Returns a Google Material Symbols (Rounded) name for any TreeNode
 * based solely on `kind` and `metadata.mediaKind`. Zero domain branches —
 * the mapping is a pure data table, not a switch on `domain`.
 *
 * Per OBJECT_IDENTITY_AND_SELECTION_POLICY §7: a per-domain icon set
 * is a policy violation. Per 50-ui-design-brief.md §5 / DEC-50-02.
 *
 * Test: no string `domain ===` appears in this file.
 */

import type { TreeNode, TreeNodeKind } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

// ─────────────────────────────────────────────────────────────────────────────
// Icon lookup table: kind → default + mediaKind overrides
// ─────────────────────────────────────────────────────────────────────────────

type KindIconEntry = {
  default: string;
  mediaKind?: Record<string, string>;
};

const KIND_ICON_MAP: Record<TreeNodeKind, KindIconEntry> = {
  region: {
    default: "public",
  },
  site: {
    default: "place",
  },
  asset: {
    default: "domain",
    mediaKind: {
      "3dtiles":    "apartment",
      "ifc-glb":    "domain",
      "pointcloud": "scatter_plot",
      "panorama":   "360",
      "raster":     "satellite_alt",
      "bim":        "architecture",
    },
  },
  group: {
    default: "folder",
  },
  discipline: {
    default: "category",
  },
  element: {
    default: "widgets",
    mediaKind: {
      "pano": "panorama",
    },
  },
  panorama: {
    default: "panorama",
  },
  document: {
    default: "description",
    mediaKind: {
      "pdf": "picture_as_pdf",
      "svg": "schema",
    },
  },
  sensor: {
    default: "sensors",
  },
  inspection: {
    default: "fact_check",
  },
  scan: {
    default: "scatter_plot",
  },
  folder: {
    default: "folder",
  },
  federation: {
    default: "link",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a Material Symbols icon name for the given TreeNode.
 *
 * Resolution order:
 *   1. `KIND_ICON_MAP[node.kind].mediaKind[node.metadata.mediaKind]`
 *   2. `KIND_ICON_MAP[node.kind].default`
 *   3. Fallback `"deployed_code"` for unknown kinds.
 *
 * Branch policy: only `kind` and `mediaKind` are inspected — never `domain`.
 */
export function getIconForNode(node: TreeNode): string {
  const entry = KIND_ICON_MAP[node.kind];
  if (!entry) return "deployed_code";

  const mediaKind =
    typeof node.metadata?.mediaKind === "string"
      ? (node.metadata.mediaKind as string)
      : undefined;

  if (mediaKind && entry.mediaKind?.[mediaKind]) {
    return entry.mediaKind[mediaKind];
  }

  return entry.default;
}
